import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Collapse,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import { ListingResource, PropertyField as Field } from '../../system/field';
import {
  formatAddressLineOne,
  formatAddressLineTwo,
} from '../../system/location';
import { usePrimaryPhoto } from '../edit/hooks/usePrimaryPhoto';
import { useSidePanel } from '../../contexts/SidePanelContext';
import { DebugCaptureResult, DebugRequestPanel } from './DebugRequestPanel';

interface PublishConfirmationProps {
  getResourceValue: (
    resourceType: ListingResource,
    fieldName: string,
    fallbackValue: any,
  ) => string;
  onListingsClick: () => void;
  listingId?: string;
  debugInfo?: DebugCaptureResult;
}

const PublishConfirmation: React.FC<PublishConfirmationProps> = ({
  getResourceValue,
  onListingsClick,
  listingId,
  debugInfo,
}) => {
  const [debugExpanded, setDebugExpanded] = useState(false);
  const { primaryPhotoUrl: listingPhoto } = usePrimaryPhoto();
  const { setPanelWidth } = useSidePanel();

  const handleDebugToggle = (checked: boolean) => {
    setDebugExpanded(checked);
    // Resize panel: 1000px when debug expanded, 375px when collapsed
    setPanelWidth(checked ? 1000 : 375);
  };
  // Get individual address components
  const streetNumber = getResourceValue(
    ListingResource.Property,
    Field.STREET_NUMBER,
    '',
  );
  const streetName = getResourceValue(
    ListingResource.Property,
    Field.STREET_NAME,
    '',
  );
  const streetDirPrefix = getResourceValue(
    ListingResource.Property,
    Field.STREET_DIR_PREFIX,
    '',
  );
  const streetDirSuffix = getResourceValue(
    ListingResource.Property,
    Field.STREET_DIR_SUFFIX,
    '',
  );
  const streetSuffix = getResourceValue(
    ListingResource.Property,
    Field.STREET_SUFFIX,
    '',
  );
  const streetModifier = getResourceValue(
    ListingResource.Property,
    Field.STREET_SUFFIX_MODIFIER,
    '',
  );
  const unitNumber = getResourceValue(
    ListingResource.Property,
    Field.UNIT_NUMBER,
    '',
  );
  const city = getResourceValue(ListingResource.Property, Field.CITY, '');
  const state = getResourceValue(
    ListingResource.Property,
    Field.STATE_OR_PROVINCE,
    '',
  );
  const zipCode = getResourceValue(
    ListingResource.Property,
    Field.POSTAL_CODE,
    '',
  );
  const zipCodePlus4 = getResourceValue(
    ListingResource.Property,
    Field.POSTAL_CODE_PLUS4,
    '',
  );

  // Format address into two lines
  const addressLine1 = formatAddressLineOne({
    streetModifier,
    streetDirPrefix,
    streetDirSuffix,
    streetName,
    streetNumber,
    streetSuffix,
    unitNumber,
  });
  const addressLine2 = formatAddressLineTwo({
    zipCode,
    zipCodePlus4,
    city,
    state,
  });

  listingId =
    listingId ||
    getResourceValue(ListingResource.Property, Field.LISTING_ID, 'Not Set');

  return (
    <Box
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="body1" sx={{ color: '#1A1A1E' }}>
        The following listing has been successfully published:
      </Typography>

      <Box
        sx={{
          width: '100%',
          aspectRatio: '4/3',
          bgcolor: '#F5F5F5',
          overflow: 'hidden',
          borderRadius: 1,
        }}
      >
        {listingPhoto ? (
          <img
            src={listingPhoto}
            alt={addressLine1}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#F5F5F5',
              color: '#767678',
            }}
          >
            <Typography variant="body1">No Photo Set</Typography>
          </Box>
        )}
      </Box>

      <Typography sx={{ fontWeight: 700, mt: 2 }}>{addressLine1}</Typography>
      <Typography sx={{ fontWeight: 700 }}>{addressLine2}</Typography>

      <Typography sx={{ fontWeight: 500, mt: 1 }}>MLS# {listingId}</Typography>

      {/* Debug Information */}
      {debugInfo && (
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={debugExpanded}
                onChange={(e) => handleDebugToggle(e.target.checked)}
                size="small"
                icon={<BugReportIcon />}
                checkedIcon={<BugReportIcon />}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Show Requests ({debugInfo.main.length + debugInfo.other.length})
              </Typography>
            }
          />
          <Collapse in={debugExpanded}>
            <Box
              sx={{
                mt: 1,
                mx: -3, // Negative margin to extend to panel edges
                mb: -1,
              }}
            >
              <DebugRequestPanel debugInfo={debugInfo} title="Publish Debug" />
            </Box>
          </Collapse>
        </Box>
      )}

      <Button
        variant="contained"
        onClick={onListingsClick}
        sx={{
          mt: 2,
          bgcolor: '#F13C00',
          '&:hover': {
            bgcolor: '#d63600',
          },
          textTransform: 'none',
        }}
      >
        Go To My Listings
      </Button>
    </Box>
  );
};

export default PublishConfirmation;
